<template>
  <BoxWithTitle>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <template #header>
      <v-toolbar-title>
        {{ $t('legionellaChart.pageName') }} {{ networkName }}
      </v-toolbar-title>
    </template>

    <template #icons>
      <v-btn
        v-if="hasPermission('flowDiagrams.add') || hasPermission('flowDiagrams.edit')"
        text
        :disabled="!isSavable"
        @click="onClickSave"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </template>

    <template #content>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VFileDragAndDrop
            v-if="hasPermission('flowDiagrams.add')"
            :file="newLegionellaChart.chart"
            :mimes="mimes"
            @onChange="onChange"
            @removeFile="removeFile"
          />
          <div>
            <VTableLocal
              :items="displayedFiles"
              :isSortable="false"
              :disablePagination="true"
              :headers="tableHeaders"
              @iconAction="onClickAction"
            >
              <template #body="{items}">
                <draggable
                  :list="items"
                  :options="{disabled: false}"
                  tag="tbody"

                  @change="onDragChange"
                >
                  <tr
                    v-for="(item,index) in items"
                    :key="index"
                  >
                    <template v-for="(headerN,indexN) in tableHeaders">
                      <td
                        v-if="headerN.value === 'drag_drop'"
                        :key="`${index}_${indexN}_drag`"
                      >
                        <template>
                          <v-icon
                            large
                            class="hidden-md-and-down"
                            color="primary"
                          >
                            mdi-drag-vertical
                          </v-icon>
                          <span @click="activateTooltip(item.order)"> {{ item.order }} </span>
                        </template>
                      </td>
                      <td
                        v-else-if="headerN.value==='action'"
                        :key="`${index}_${indexN}_action`"
                      >
                        <template v-if="actions">
                          <div class="action-buttons">
                            <VTableAction
                              :itemActions="actions"
                              :verticalActionDots="true"
                              :isCustomIcon="true"
                              @iconAction="(action)=>onClickAction(item, action)"
                            />
                          </div>
                        </template>
                      </td>
                      <td
                        v-else-if="headerN.value==='filename'"
                        :key="`${index}_${indexN}_others`"
                      >
                        <span>{{ item.fileName || item[headerN.value] }}</span>
                        <div
                          v-if="isTooltipActive(item.order)"
                          class="custom-drag-input"
                        >
                          <div class="wrapper-div">
                            <v-text-field
                              v-model="customDragItemPosition"
                              @keydown.enter="onManualPositionInput(item)"
                            />
                          </div>
                        </div>
                      </td>
                      <td
                        v-else
                        :key="`${index}_${indexN}_others`"
                      >
                        {{ item[headerN.value] }}
                      </td>
                    </template>
                  </tr>
                </draggable>
              </template>
            </VTableLocal>
          </div>
        </v-col>
      </v-row>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import VFileDragAndDrop from '@/components/shared/widgets/forms/VuetifyInputs/VFileDragAndDrop';
import draggable from 'vuedraggable';
import VTableAction from '@/components/shared/widgets/VTableAction';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';
import RedirectModal from '@/components/shared/RedirectModal.vue';


export default {
    components: {
        RedirectModal,
        BoxWithTitle,
        VTableLocal,
        VFileDragAndDrop,
        draggable,
        VTableAction,

    },
    data() {
        return {
            showRedirectModal: false,
            legionellaChart: null,
            isSavable: false,
            newLegionellaChart: {
                chart: null,
            },
            files: [],
            showTooltipIndex: -1,
            mimes: ['image/png', 'image/gif', 'image/jpeg'],
            localComponentId: null,
            actionName: 'legionellaChart',
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'hasPermission',
            'lang',
            'preventQuitFormular',
        ]),
        rules() {
            return {
                size: 10000,
                mimes: this.mimes,
            };
        },
        isNew() {
            return isEmpty(this.legionellaChart);
        },
        networkName() {
            const networkName = `${this.network.name} (${this.network.type.key})`;
            if (!isEmpty(this.component)) {
                return `${networkName} -> ${this.component.name} (${this.component.type.key})`;
            }

            return networkName;
        },
        tableHeaders() {
            return [
                'drag_drop',
                'filename',
                'size',
                'uploadedBy',
                'addedDate',
                'action',
            ].map(value => ({
                text: value === 'drag_drop' ? '' : this.$t(`legionellaChart.${value}`),
                value,
            }));
        },
        displayedFiles() {
            return this.legionellaChart?.chart.length ? this.legionellaChart.chart.map(d => ({
                ...d,
                size: `${Math.floor(d.size / 1000)}KB`,
                addedDate: this.$options.filters.formatDate(d.createdAt),
            })) : [];
        },
        actions() {
            const actions = [];

            if (this.hasPermission('files.view')) {
                actions.push({
                    name: this.$t('legionellaChart.preview'),
                    icon: 'mdi-eye',
                });
            }

            if (this.hasPermission('files.remove')) {
                actions.push({
                    name: this.$t('legionellaChart.deleteIcon'),
                    icon: 'delete',
                    iconAndTextColor: 'red',
                });
            }

            return actions;
        },

    },
    watch: {
        isSavable: {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                }
            },
        },
        'legionellaChart.chart': {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                } else {
                    this.disablePreventQuitFormular();
                }
            },
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.localComponentId) {
            lockService.releaseLock(this.localComponentId, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    async created() {
        if (this.network.id) {
            await this.loadData();
        }
        this.localComponentId = this.$route.params.componentId || this.component?.id;
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.localComponentId) {
                const releaseStatus = await lockService.releaseLock(this.localComponentId, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    methods: {
        ...mapActions({
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        async onClickSave() {
            this.isSavable = false;
            // check here
            if (this.isNew) {
                return this.addLegionellaControlChart();
            }

            return this.updateLegionellaControlChart();
        },
        handleUnload() {
            if (this.localComponentId) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.localComponentId, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.localComponentId, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.localComponentId, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        preparePayload() {
            const formData = new FormData();
            formData.append('chart', this.newLegionellaChart.chart);
            if (!isEmpty(this.component)) { formData.append('component', this.component.id); }
            return formData;
        },
        async loadData() {
            const queryParams = !isEmpty(this.component.id) ? { component: this.component.id } : {};
            const { data } = await this.$rq.getLegionellaControlCharts(this.network.id, queryParams);
            [this.legionellaChart] = data;
        },
        async addLegionellaControlChart() {
            const payload = this.preparePayload();

            const { data } = await this.$rq.addLegionellaControlChart(this.network.id, payload);
            this.legionellaChart = data;

            this.$toasted.global.toast_success({
                message: this.$t('legionellaChart.added'),
            });
            await this.resetAll();
        },
        async updateLegionellaControlChart() {
            const payload = this.preparePayload();
            await this.$rq.updateLegionellaControlChart(this.network.id, this.legionellaChart.id, payload);

            this.$toasted.global.toast_success({
                message: this.$t('legionellaChart.updated'),
            });
            await this.resetAll();
        },
        onClickDeleteLegionellaControlChart(file) {
            if (!this.legionellaChart || !this.hasPermission('files.remove') || !this.hasPermission('flowDiagrams.remove')) {
                return;
            }
            this.$root.$emit('showModal', {
                title: this.$t('legionellaChart.delete'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('legionellaChart.confirmRemove') },
                onConfirm: () => this.deleteLegionellaControlChart(file),
                isForm: false,
            });
        },
        async deleteLegionellaControlChart(file) {
            await this.$rq.deleteLegionellaControlChart(this.network.id, this.legionellaChart.id, file.id);
            this.legionellaChart = null;

            this.$toasted.global.toast_success({
                message: this.$t('legionellaChart.deleted'),
            });
            await this.resetAll();
        },
        onChange(file) {
            this.newLegionellaChart.chart = file;
            this.isSavable = true;
        },
        removeFile() {
            this.newLegionellaChart.chart = null;
            this.isSavable = false;
        },
        async resetAll() {
            this.newLegionellaChart.chart = null;
            this.isSavable = false;
            await this.loadData();
        },
        async previewFile(file) {
            if (this.hasPermission('files.view')) {
                const { id, fileName } = file;
                const { data } = await this.$rq.downloadFile(id);
                this.$utils.saveFileOnDisk(data, fileName);
            }
        },
        async onClickAction(item, action) {
            switch (action.icon) {
                case 'mdi-eye':
                    await this.previewFile(item);
                    break;
                case 'delete':
                    this.onClickDeleteLegionellaControlChart(item);
                    break;
                default:
                    break;
            }
        },
        async onDragChange(e) {
            const { element: file, newIndex } = e.moved;
            this.showTooltipIndex = -1;
            await this.$rq.updateLegionellaControlChartPosition(this.network.id, this.legionellaChart.id, file.id, { newIndex });
            await this.loadData();
        },
        onManualPositionInput(item) {
            if (this.customDragItemPosition !== this.showTooltipIndex) {
                this.onDragChange({ moved: { element: item, newIndex: this.customDragItemPosition } });
            }
        },
        isTooltipActive(currentIndex) {
            return this.showTooltipIndex === currentIndex;
        },
        activateTooltip(currentIndex) {
            this.showTooltipIndex = currentIndex;
            this.customDragItemPosition = currentIndex;
        },
    },
};
</script>
<style lang='scss' scoped>
  .custom-drag-input {
        position: relative;
        width: 18%;
        .wrapper-div{
          position: absolute;
          top: 8px;
          height: 33px;
          padding: 0 5px;
          outline: 1px solid;
          background-color: white;
          .v-text-field{
            padding-top: 0;
            margin-top: 0;
          }
        }
      }

  @media screen and (max-width: 800px) {
    .drag-container {
      overflow: scroll ;
      margin-left: 55px !important;
      padding-left: 0;
    }
  }

</style>
